import { useEffect, useState } from 'react';
import cx from 'classnames';

import { Icon, Text } from '../../common/components';
import { t } from '../../common/i18n';
import { formatDate } from '../../common/utils/Date/Date';
import { resolveCategoryIcon } from '../config/issueConfig';
import { fetchIssuePhotoUrl } from '../services/IssueService';
import { Issue } from '../types';
import IssueStatusBadge from './IssueStatusBadge';

const namespace = 'Issues';

const THUMBNAIL_WIDTH = 480;

interface IssueCardProps {
  issue: Issue;
  onOpen: (issue: Issue) => void;
}

/**
 * Fotokarta hlášení: cover = zmenšenina první fotky, bez fotky placeholder
 * s ikonou kategorie. Uzavřená hlášení (vyřešeno/zamítnuto) jsou ztlumená,
 * aby aktivní věci vynikly.
 */
export const IssueCard: React.FC<IssueCardProps> = ({ issue, onOpen }) => {
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const isClosed = issue.status === 'resolved' || issue.status === 'rejected';
  const firstPhoto = issue.photos[0] ?? null;

  useEffect(() => {
    if (!firstPhoto) return undefined;

    let cancelled = false;
    let url: string | null = null;

    fetchIssuePhotoUrl(issue.id, firstPhoto, THUMBNAIL_WIDTH)
      .then((objectUrl) => {
        url = objectUrl;
        if (!cancelled) setCoverUrl(objectUrl);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issue.id, firstPhoto?.id]);

  return (
    <button type="button" className={cx('IssueCard', isClosed && 'IssueCard--closed')} onClick={() => onOpen(issue)}>
      <div className="IssueCard__cover">
        {coverUrl ? (
          <img src={coverUrl} alt="" className="IssueCard__photo" />
        ) : (
          <span className="IssueCard__placeholder">
            <Icon name={resolveCategoryIcon(issue.category.icon)} />
          </span>
        )}

        <IssueStatusBadge status={issue.status} className="IssueCard__badge" />

        {issue.photos.length > 0 && (
          <span className="IssueCard__chip IssueCard__chip--photos">
            <Icon name="camera" /> {issue.photos.length}
          </span>
        )}

        {issue.comments.length > 0 && (
          <span className="IssueCard__chip IssueCard__chip--comments">
            <Icon name="message" /> {t('commentCount', namespace, { count: issue.comments.length })}
          </span>
        )}
      </div>

      <div className="IssueCard__body">
        <Text as="div" className="IssueCard__title" size="body-medium" weight="600" color="Neutral800">
          {issue.title}
        </Text>
        <Text as="div" className="IssueCard__meta" size="body-small" color="Neutral600">
          {issue.category.name} <span className="IssueCard__dot">·</span> {issue.authorName}{' '}
          <span className="IssueCard__dot">·</span> {formatDate(issue.createdAt)}
        </Text>
      </div>
    </button>
  );
};

export default IssueCard;
